import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { Character, GameSettings, Maturity, Player, Prompt, PromptPack } from "../types";
import { GameStatus } from "../types/game-status";

/*
 * REDUX SELECTOR DOCUMENTATION
 *
 * These selectors provide safe, predictable data access patterns.
 * Key principles:
 * 1. Always return a valid value (no undefined/null) by providing sensible defaults.
 * 2. This eliminates the need for optional chaining (`?.`) in components.
 * 3. Return types are predictable and documented.
 */

// === Game Selectors ===

/** Default game settings - defined as a constant to maintain referential stability */
const DEFAULT_GAME_SETTINGS: GameSettings = {
  num_characters_submit: 3,
  num_characters_select: 2,       // Number of characters players must select
  write_duration: 180,
  num_switches: 2,
  show_instructions: false,
  maturity: Maturity.ALL_AGES,
  hide_authors: false,
  max_players: 8,

  moderation: false,
  self_shuffle: false,
  audiences: false,
  multi_prompt: false,
  prompt_packs: [] as PromptPack[],
  no_extra_reading_time: false,
  character_maturity: false,
  forced_prompts: false,
  allow_duplicate_characters: false,
  auto_sync_parts_to_players: false,
};

/** Gets complete game settings with guaranteed defaults. Always returns a complete object. */
export const selectGameSettings = (state: RootState) =>
  state.game.game?.settings ?? DEFAULT_GAME_SETTINGS;

/** Gets current game status as a string. Always returns a GameStatus, defaulting to Lobby. */
export const selectGameStatus = (state: RootState): GameStatus =>
  state.game.game?.status_str ?? GameStatus.GameStatusLobby;

/** Default countdown - defined as a constant to maintain referential stability */
const DEFAULT_COUNTDOWN = { deadline: 0 } as const;

/** Gets game countdown timer. Always returns an object with `deadline`. */
export const selectCountdown = (state: RootState) =>
  state.game.game?.countdown ?? DEFAULT_COUNTDOWN;

/** Checks if the current game mode is solo. Always returns a boolean. */
export const selectIsSolo = (state: RootState) =>
  !!selectGameSettings(state).solo_writing;

/** Checks if the current player is the host. Always returns a boolean. */
export const selectIsHost = (state: RootState) =>
  state.player.player?.id === state.game.game?.host_id;

// === Player Selectors ===

/** Gets the current player object. May be undefined if the player state isn't set. */
export const selectPlayer = (state: RootState) => state.player.player;

/** Empty characters array - defined as a constant to maintain referential stability */
const EMPTY_CHARACTERS: Character[] = [];

/** Gets player characters array. Always returns an array, empty if none. */
export const selectPlayerCharacters = (state: RootState) =>
  state.player.player?.characters ?? EMPTY_CHARACTERS;

/** Gets player's current game status. Always returns a GameStatus, defaulting to Lobby. */
export const selectPlayerStatus = (state: RootState): GameStatus =>
  state.player.player?.status_str ?? GameStatus.GameStatusLobby;

/** Default player meta - defined as a constant to maintain referential stability */
const DEFAULT_PLAYER_META = {
  sprite_id: 0,
  mature: false,
  accessibility: false,
  accessibility2: 0,
} as const;

/** Gets player metadata with defaults. Always returns a complete object. */
export const selectPlayerMeta = (state: RootState) =>
  state.player.player?.meta ?? DEFAULT_PLAYER_META;

/** Empty prompts array - defined as a constant to maintain referential stability */
const EMPTY_PROMPTS: Prompt[] = [];

/** Gets player prompts array. Always returns an array, empty if none. */
export const selectPlayerPrompts = (state: RootState) =>
  state.player.player?.prompts ?? EMPTY_PROMPTS;

/** Default player data - defined as a constant to maintain referential stability */
const DEFAULT_PLAYER_DATA = {
  audience: false,
  points: 0,
  vip: false,
  connected: true,
  premium: false,
} as const;

/** Gets player data with defaults. Always returns a complete object. */
export const selectPlayerData = (state: RootState) =>
  state.player.player?.data ?? DEFAULT_PLAYER_DATA;

/** Gets the current submission. Can return undefined - check before use. */
export const selectPlayerCurrentSubmission = (state: RootState) =>
  state.player.player?.current_submission;

/** Gets the current story. Can return null - check before use. */
export const selectPlayerCurrentStory = (state: RootState) =>
  state.player.player?.current_story ?? null;

/** Gets accessibility setting. Always returns a number. */
export const selectAccessibility2 = (state: RootState) =>
  state.player.player?.meta?.accessibility2 ?? 0;

// === Room/UI Selectors ===

/** Gets audience count. Always returns a number. */
export const selectAudienceCount = (state: RootState) =>
  state.game.game?.extra?.num_audience ?? 0;

/** Gets room code. Always returns a string, empty if no room. */
export const selectRoomCode = (state: RootState) =>
  state.menu.playerId?.room_code ?? "";

// === Meta Selectors ===

/** Gets connection status. Always returns the current status. */
export const selectConnectionStatus = (state: RootState) =>
  state.meta.connectionStatus;

/** Gets loading state. Always returns a boolean. */
export const selectLoading = (state: RootState) => state.meta.loading;

/** Gets error message. Always returns a string, empty if no error. */
export const selectError = (state: RootState) => state.meta.error ?? "";

/** Empty streamers array - defined as a constant to maintain referential stability */
const EMPTY_STREAMERS: string[] = [];

/** Gets streamers array. Always returns an array, empty if none. */
const selectStreamers = (state: RootState) => state.meta.streamers ?? EMPTY_STREAMERS;

// === Menu Selectors ===

/** Gets player details from the menu slice. Can be undefined - check before use. */
export const selectPlayerDetails = (state: RootState) =>
  state.menu.playerDetails;

/** Gets user info. Can be undefined - check before use. */
const selectUser = (state: RootState) => state.menu.user;

// === Computed Selectors ===

/** Gets characters sorted by locked status. Memoized for performance. Always returns an array. */
export const sortedCharactersSelector = createSelector(
  selectPlayerCharacters,
  (characters) =>
    [...characters].sort((a, b) => {
      if (a.meta.locked && !b.meta.locked) return -1;
      if (!a.meta.locked && b.meta.locked) return 1;
      return a.id - b.id;
    })
);

/** Checks if the player is in the audience. Memoized for performance. Always returns a boolean. */
export const selectIsAudience = createSelector(
  selectPlayerData,
  (playerData) => playerData.audience
);

/** Empty players array - defined as a constant to maintain referential stability */
const EMPTY_PLAYERS: Player[] = [];

/** Gets all players from the game state. Always returns an array. */
export const selectPlayers = (state: RootState): Player[] =>
  state.game.game?.players ?? EMPTY_PLAYERS;
